import React, { ReactNode, useEffect, useMemo, useRef } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PointElement,
  Tooltip,
} from "chart.js";
import DataLabels from "chartjs-plugin-datalabels";
import { head } from "lodash";
import { Chart } from "react-chartjs-2";
import styled from "styled-components";
import { Item } from "@charty/legend/item/model";
import { GlossyTheme } from "@glossy/theme/types";
import { AbstractDate, Range } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import { Serie } from "../models";
import {
  includeEndOfRange,
  updateOnHover,
  useDatesFromRanges,
  useFormatData,
  useOnClickItem,
} from "./data";
import { getOptions } from "./options";

ChartJS.register(
  BarElement,
  CategoryScale,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PointElement,
  DataLabels
);

type ChartTooltip = {
  count: number;
  label: string;
  color: string;
  date: AbstractDate;
  total?: number;
};
interface IProps {
  series: Serie[];
  range: Range;
  locale: Locale;
  onClickItem?: (
    uid: Item["id"],
    range: { from: AbstractDate; to: AbstractDate }
  ) => void;
  chartTooltip?: (props: ChartTooltip[]) => ReactNode;
  scale?: "linear" | "logarithmic";
  theme: GlossyTheme;
  hovered: string;
  timezone: Timezone;
  disableAnimation?: boolean;
  isFullscreen?: boolean;
}

const Root = styled.div`
  position: relative;
  height: calc(100% - 36px);
`;

const DoubleTimechartChart = ({
  series,
  range,
  locale,
  onClickItem,
  chartTooltip,
  scale = "linear",
  theme,
  hovered,
  timezone,
  disableAnimation,
  isFullscreen,
}: IProps) => {
  const chartRef = useRef(null);

  const withEndOfRange = includeEndOfRange(head(series)?.data, range);
  const datesFromRanges = useDatesFromRanges(range, withEndOfRange);
  const formatData = useFormatData(series, range, theme);
  const _onClickItem = useOnClickItem(series, range, onClickItem);

  useEffect(() => {
    if (chartRef.current) {
      updateOnHover(chartRef.current, hovered, theme);
      chartRef.current.update();
    }
  }, [hovered, chartRef, theme]);

  const options = useMemo(
    () =>
      getOptions(
        datesFromRanges,
        range.interval,
        theme,
        locale,
        timezone,
        onClickItem ? _onClickItem : null,
        series,
        chartTooltip,
        scale,
        disableAnimation,
        isFullscreen
      ),
    [
      datesFromRanges,
      range.interval,
      theme,
      locale,
      timezone,
      onClickItem,
      _onClickItem,
      series,
      chartTooltip,
      scale,
      disableAnimation,
      isFullscreen,
    ]
  );

  return (
    <Root>
      <Chart type="bar" data={formatData} options={options} ref={chartRef} />
    </Root>
  );
};

export default DoubleTimechartChart;
